import { useState } from "react";
import * as Icons from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

/**
 * Governed duplicate-resolution dialog — identical everywhere.
 * Options: View Existing · Keep as New Version · Keep as Separate Document · Cancel.
 * onResolve(choice, { note }) — choice ∈ 'view' | 'version' | 'separate' | 'cancel'.
 */
const fmt = (s) => { try { return new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); } catch { return s; } };

export function DuplicateDialog({ open, existing, incomingName, allowSeparate = true, onResolve }) {
  const [choice, setChoice] = useState("version");
  const [note, setNote] = useState("");
  if (!existing) return null;
  const curV = existing.version || 1;

  const OPTIONS = [
    { key: "view", label: "View Existing", desc: "Open the document already on file.", icon: "Eye" },
    { key: "version", label: "Keep as New Version", desc: `Supersede v${curV} with an updated copy.`, icon: "GitBranch" },
    ...(allowSeparate ? [{ key: "separate", label: "Keep as Separate Document", desc: "A distinct document (e.g. a signed or annotated copy).", icon: "Files" }] : []),
    { key: "cancel", label: "Cancel Upload", desc: "Discard this file.", icon: "X" },
  ];

  return (
    <Dialog open={!!open} onOpenChange={(v) => { if (!v) onResolve("cancel", {}); }}>
      <DialogContent className="max-w-md" data-testid="duplicate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Icons.Copy className="h-4 w-4 text-amber-600" />Potential Duplicate Detected</DialogTitle>
          <DialogDescription className="sr-only">Choose how to resolve a duplicate document upload.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800" data-testid="duplicate-existing">
            <div>Existing: <strong>{existing.filename}</strong> · v{curV}{existing.uploaded_at ? ` · ${fmt(existing.uploaded_at)}` : ""}</div>
            {incomingName && <div className="mt-0.5">Uploading: <strong>{incomingName}</strong></div>}
          </div>

          <div className="grid grid-cols-1 gap-1.5">
            {OPTIONS.map((o) => {
              const Ic = Icons[o.icon] || Icons.Circle; const active = choice === o.key;
              return (
                <button key={o.key} type="button" onClick={() => setChoice(o.key)} data-testid={`duplicate-opt-${o.key}`}
                  className={`flex items-start gap-2 text-left rounded-lg border px-3 py-2 transition-all ${active ? "border-blue-500 bg-blue-50/60" : "border-slate-200 hover:border-slate-300"}`}>
                  <Ic className={`h-4 w-4 mt-0.5 ${active ? "text-blue-600" : "text-slate-400"}`} />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800">{o.label}</div>
                    <div className="text-[11px] text-slate-500">{o.desc}</div>
                  </div>
                  {active && <Icons.Check className="h-3.5 w-3.5 ml-auto text-blue-600 mt-0.5" />}
                </button>
              );
            })}
          </div>

          {choice === "version" && (
            <div data-testid="duplicate-version-intel" className="rounded-lg border border-slate-200 p-3">
              <div className="flex items-center justify-center gap-3 text-xs">
                <span className="px-2 py-1 rounded bg-slate-100 text-slate-600">Current · v{curV}</span>
                <Icons.ArrowRight className="h-3.5 w-3.5 text-slate-400" />
                <span className="px-2 py-1 rounded bg-blue-100 text-blue-700 font-medium">Incoming · v{curV + 1}</span>
              </div>
              <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="duplicate-version-note"
                placeholder="Optional note (e.g. Client sent corrected copy)"
                className="mt-2 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            </div>
          )}
        </div>
        <DialogFooter>
          <button data-testid="duplicate-confirm" onClick={() => onResolve(choice, { note })}
            className="text-sm px-4 py-2 rounded-lg bg-blue-600 text-white font-medium">
            {choice === "view" ? "View" : choice === "cancel" ? "Cancel Upload" : "Confirm"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DuplicateDialog;
